/**
 * NotificationToast — individual toast notification component.
 * Renders a single notification with icon, message, optional error code,
 * copy-to-clipboard, action button, and auto-dismiss progress bar.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { Notification } from '../../store/notificationStore';
import { useNotificationStore } from '../../store/notificationStore';
import {
  StatusSuccess as CheckCircleIcon,
  StatusInfo as InfoCircleIcon,
  StatusWarning as AlertTriangleIcon,
  StatusDanger as XCircleIcon,
  Spinner as SpinnerIcon,
  Close as CloseIcon,
  Copy as CopyIcon,
} from '../icons';
import './NotificationToast.css';

interface NotificationToastProps {
  notification: Notification;
}

const ICON_MAP = {
  success: CheckCircleIcon,
  info: InfoCircleIcon,
  warning: AlertTriangleIcon,
  error: XCircleIcon,
  loading: SpinnerIcon,
} as const;

export const NotificationToast = ({ notification }: NotificationToastProps) => {
  const { dismiss, paused } = useNotificationStore();
  const [exiting, setExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const remainingRef = useRef(notification.autoCloseDelay);
  const startTimeRef = useRef(Date.now());

  const handleDismiss = useCallback(() => {
    setExiting(true);
    // Wait for exit animation before store dismiss
    setTimeout(() => dismiss(notification.id), 250);
  }, [dismiss, notification.id]);

  // Auto-dismiss timer with pause support
  useEffect(() => {
    if (!notification.autoClose || notification.autoCloseDelay <= 0) return;

    const tick = () => {
      if (paused) return;
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = notification.autoCloseDelay - elapsed;
      remainingRef.current = remaining;
      setProgress(Math.max(0, (remaining / notification.autoCloseDelay) * 100));

      if (remaining <= 0) {
        handleDismiss();
      }
    };

    startTimeRef.current = Date.now() - (notification.autoCloseDelay - remainingRef.current);
    timerRef.current = setInterval(tick, 50);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [notification.autoClose, notification.autoCloseDelay, paused, handleDismiss]);

  // Pause/resume: reset start time when paused state changes
  useEffect(() => {
    if (!paused) {
      startTimeRef.current = Date.now() - (notification.autoCloseDelay - remainingRef.current);
    }
  }, [paused, notification.autoCloseDelay]);

  const handleCopyDetails = useCallback(() => {
    const details = [
      notification.code && `Code: ${notification.code}`,
      notification.correlationId && `Request: ${notification.correlationId}`,
      `Message: ${notification.message}`,
    ].filter(Boolean).join(' | ');

    navigator.clipboard.writeText(details).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [notification.code, notification.correlationId, notification.message]);

  const handleAction = useCallback(() => {
    notification.onAction?.();
    handleDismiss();
  }, [notification, handleDismiss]);

  const Icon = ICON_MAP[notification.type];
  const showMeta = notification.code || notification.correlationId;
  const showCopy = notification.type === 'error' || notification.type === 'warning';

  return (
    <div
      className={`notification-toast notification-toast--${notification.type}${exiting ? ' notification-toast--exiting' : ''}`}
      role={notification.type === 'error' || notification.type === 'warning' ? 'alert' : 'status'}
      aria-live={notification.type === 'error' || notification.type === 'warning' ? 'assertive' : 'polite'}
      onMouseEnter={() => useNotificationStore.getState().setPaused(true)}
      onMouseLeave={() => useNotificationStore.getState().setPaused(false)}
    >
      <div className="notification-toast__icon">
        <Icon />
      </div>

      <div className="notification-toast__content">
        {notification.title && (
          <p className="notification-toast__title">{notification.title}</p>
        )}
        <p className="notification-toast__message">{notification.message}</p>

        {showMeta && (
          <div className="notification-toast__meta">
            {notification.code && (
              <span className="notification-toast__code">{notification.code}</span>
            )}
            {showCopy && (
              <button
                className="notification-toast__copy-btn"
                onClick={handleCopyDetails}
                type="button"
                aria-label="Copy error details"
              >
                <CopyIcon />
                {copied ? 'Copied' : 'Copy details'}
              </button>
            )}
          </div>
        )}

        {notification.actionLabel && notification.onAction && (
          <div className="notification-toast__actions">
            <button
              className="notification-toast__action-btn"
              onClick={handleAction}
              type="button"
            >
              {notification.actionLabel}
            </button>
          </div>
        )}
      </div>

      {notification.type !== 'loading' && (
        <button
          className="notification-toast__close"
          onClick={handleDismiss}
          type="button"
          aria-label="Dismiss notification"
        >
          <CloseIcon />
        </button>
      )}

      {notification.autoClose && notification.autoCloseDelay > 0 && (
        <div
          className="notification-toast__progress"
          style={{ width: `${progress}%` }}
        />
      )}
    </div>
  );
};
